import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Download, FileSpreadsheet, Loader2, RefreshCw, Upload, Users, Wallet, Phone, Scale, Activity, ClipboardList, X, ChevronDown, ChevronRight,
  Stethoscope, CircleX, TrendingUp, Timer, LayoutGrid, ArrowDown, ArrowUp, GitCompare, ChevronUp,
} from "lucide-react";
import TlpLeaderComparisonView from "@/components/reports/TlpLeaderComparisonView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { parseTlpCiroWorkbook } from "@/utils/tlpCiroExcelImport";
import { parseMrBtWorkbook } from "@/utils/tlpMrExcelImport";
import MrExcelImportModal from "@/components/reports/MrExcelImportModal";
import {
  getTodayStr, getYesterdayStr, getLastWeekRange, getLastMonthRange, getThisMonthRange,
} from "@/utils/helpers";
import "./tlp-performance-loader.css";

const TLP_LOADING_STEPS = [
  { key: "ciro", label: "Ciro · işlem · muayene", icon: Wallet },
  { key: "mr", label: "MR alma (günlük)", icon: Activity },
  { key: "kaliteli", label: "WP & Kaliteli dönüşüm", icon: Phone },
  { key: "calls", label: "Ort. görüşme & mola", icon: Timer },
  { key: "kalite", label: "TL × Kalite eşleşme", icon: Scale },
  { key: "personnel", label: "Personel giriş / çıkış", icon: ClipboardList },
];

const TlpQueryLoader = ({ isDark, textClass, labelClass, loadingStep, leaderLabel }) => {
  const progressPct = loadingStep >= 0
    ? Math.min(100, Math.round(((loadingStep + 1) / TLP_LOADING_STEPS.length) * 100))
    : 0;

  return (
    <div
      className={`relative rounded-2xl border overflow-hidden ${
        isDark
          ? "border-emerald-500/20 bg-slate-900/80"
          : "border-emerald-200/80 bg-gradient-to-b from-white to-emerald-50/60 shadow-lg shadow-emerald-500/5"
      }`}
    >
      <div className="tlp-loader-glow absolute inset-0 pointer-events-none" aria-hidden />
      <div className="relative px-6 py-10 md:py-12">
        <div className="max-w-md mx-auto text-center">
          <div className="relative w-20 h-20 mx-auto mb-5 tlp-loading-float">
            <div
              className={`absolute inset-0 rounded-full border-2 border-dashed tlp-loading-orbit ${
                isDark ? "border-emerald-400/35" : "border-emerald-400/50"
              }`}
            />
            <div
              className={`absolute inset-1 rounded-full border border-dotted tlp-loading-orbit-reverse opacity-60 ${
                isDark ? "border-teal-300/30" : "border-teal-500/35"
              }`}
            />
            <div
              className={`absolute inset-3 rounded-full flex items-center justify-center tlp-loading-pulse ${
                isDark ? "bg-emerald-500/20 ring-1 ring-emerald-500/30" : "bg-emerald-100 ring-1 ring-emerald-200"
              }`}
            >
              <BarChart3 className={`w-7 h-7 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
            </div>
          </div>
          <p className={`text-base font-semibold mb-1 ${textClass}`}>Performans raporu hazırlanıyor</p>
          <p className={`text-xs mb-5 ${labelClass}`}>
            {leaderLabel && leaderLabel !== "—" ? `${leaderLabel} · ` : ""}
            6 modül bir arada yükleniyor…
          </p>
          <div className={`h-2 rounded-full overflow-hidden mb-1 ${isDark ? "bg-slate-800" : "bg-gray-200/80"}`}>
            <div
              className="h-full rounded-full tlp-shimmer-bar transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(8, progressPct)}%` }}
            />
          </div>
          <p className={`text-[10px] tabular-nums mb-6 ${labelClass}`}>{progressPct}%</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-left max-w-lg mx-auto">
            {TLP_LOADING_STEPS.map((step, idx) => {
              const done = loadingStep > idx;
              const active = loadingStep === idx;
              const Icon = step.icon;
              return (
                <li
                  key={step.key}
                  className={`flex items-center gap-2.5 text-xs transition-all duration-300 ${
                    idx > loadingStep + 1 ? "opacity-35" : "opacity-100"
                  } ${active ? "tlp-step-active" : ""} ${done ? "tlp-step-done" : ""}`}
                >
                  <span
                    className={`tlp-step-dot w-2 h-2 rounded-full shrink-0 transition-all ${
                      done || active
                        ? (isDark ? "bg-emerald-400" : "bg-emerald-500")
                        : (isDark ? "bg-slate-600" : "bg-gray-300")
                    }`}
                  />
                  <Icon
                    className={`w-3.5 h-3.5 shrink-0 ${
                      active
                        ? (isDark ? "text-emerald-300" : "text-emerald-600")
                        : done
                          ? (isDark ? "text-emerald-400/80" : "text-emerald-600/70")
                          : (isDark ? "text-slate-500" : "text-gray-400")
                    }`}
                  />
                  <span
                    className={
                      active
                        ? (isDark ? "text-emerald-100 font-medium" : "text-emerald-900 font-medium")
                        : done
                          ? (isDark ? "text-slate-300" : "text-gray-700")
                          : labelClass
                    }
                  >
                    {step.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

const TLP_DATE_PRESETS = [
  { id: "today", label: "Bugün" },
  { id: "yesterday", label: "Dün" },
  { id: "lastWeek", label: "Geçen hafta" },
  { id: "thisMonth", label: "Bu ay" },
  { id: "lastMonth", label: "Geçen ay" },
];

const resolveTlpDatePreset = (presetId) => {
  switch (presetId) {
    case "today": {
      const d = getTodayStr();
      return { start: d, end: d };
    }
    case "yesterday": {
      const d = getYesterdayStr();
      return { start: d, end: d };
    }
    case "lastWeek":
      return getLastWeekRange();
    case "thisMonth":
      return getThisMonthRange();
    case "lastMonth":
      return getLastMonthRange();
    default:
      return null;
  }
};

const formatSeconds = (sec) => {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}s ${m}dk`;
  return `${m} dk`;
};

const formatMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("tr-TR", { maximumFractionDigits: 0 });
};

const formatCiroShort = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) {
    return `₺${(v / 1_000_000).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} Mn`;
  }
  if (v >= 10_000) {
    return `₺${(v / 1000).toLocaleString("tr-TR", { maximumFractionDigits: 0 })} B`;
  }
  return `₺${formatMoney(v)}`;
};

/** Seçili dönem özeti — tam tutar, yuvarlama yok (tam sayı gösterimi) */
const formatCiroFull = (n) => {
  if (n == null || n === "") return "—";
  return `₺${formatMoney(n)}`;
};

const formatKaliteliCount = (n) => {
  if (n == null || n === "") return "—";
  return Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 });
};

const TLP_MODULE_NAV = [
  { id: "overview", label: "Genel Bakış", icon: LayoutGrid },
  { id: "tlp-mod-1", label: "Ciro", icon: Wallet },
  { id: "tlp-mod-2", label: "MR", icon: Activity },
  { id: "tlp-mod-3", label: "WP / Kaliteli", icon: Phone },
  { id: "tlp-mod-4", label: "Görüşme", icon: Timer },
  { id: "tlp-mod-5", label: "Kalite eşleşme", icon: Scale },
  { id: "tlp-mod-6", label: "Personel", icon: ClipboardList },
];

const tlpAccent = (key, isDark) => {
  const bars = {
    emerald: "bg-emerald-500",
    violet: "bg-violet-500",
    teal: "bg-teal-500",
    sky: "bg-sky-500",
    blue: "bg-blue-500",
    amber: "bg-amber-500",
  };
  const chips = {
    emerald: isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700",
    violet: isDark ? "bg-violet-500/15 text-violet-400" : "bg-violet-50 text-violet-700",
    teal: isDark ? "bg-teal-500/15 text-teal-400" : "bg-teal-50 text-teal-700",
    sky: isDark ? "bg-sky-500/15 text-sky-400" : "bg-sky-50 text-sky-700",
    blue: isDark ? "bg-blue-500/15 text-blue-400" : "bg-blue-50 text-blue-700",
    amber: isDark ? "bg-amber-500/15 text-amber-400" : "bg-amber-50 text-amber-700",
  };
  const borders = {
    emerald: isDark ? "border-emerald-500/20" : "border-emerald-100",
    violet: isDark ? "border-violet-500/20" : "border-violet-100",
    teal: isDark ? "border-teal-500/20" : "border-teal-100",
    sky: isDark ? "border-sky-500/20" : "border-sky-100",
    blue: isDark ? "border-blue-500/20" : "border-blue-100",
    amber: isDark ? "border-amber-500/20" : "border-amber-100",
  };
  const k = bars[key] ? key : "emerald";
  return { chip: chips[k], bar: bars[k], border: borders[k] };
};

const MR_TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const formatMrAxisDay = (dateStr) => {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) return "";
  const [, m, d] = String(dateStr).split("-");
  return `${Number(d)} ${MR_TR_MONTHS[Number(m) - 1] || m}`;
};

const tlpMrNiceMax = (maxVal) => {
  const m = Math.max(0, Number(maxVal) || 0);
  if (m <= 20) return 20;
  if (m <= 40) return 40;
  if (m <= 60) return 60;
  return Math.ceil(m / 20) * 20;
};

const tlpBuildSmoothPath = (points) => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

const TlpMrSparkline = ({ days, isDark, width = 132, height = 44 }) => {
  const svg = useMemo(() => {
    const values = (days || []).map((d) => Number(d.net_count) || 0);
    if (!values.length) return null;
    const pad = { top: 4, right: 4, bottom: 4, left: 4 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const maxV = Math.max(...values, 1);
    const points = values.map((val, idx) => ({
      x: pad.left + (values.length === 1 ? innerW / 2 : (idx / (values.length - 1)) * innerW),
      y: pad.top + innerH - (val / maxV) * innerH,
    }));
    const line = tlpBuildSmoothPath(points);
    const area = `${line} L ${points[points.length - 1].x} ${pad.top + innerH} L ${points[0].x} ${pad.top + innerH} Z`;
    const stroke = isDark ? "#a78bfa" : "#7c3aed";
    const fillId = `tlp-mr-spark-${width}-${height}`;
    return { line, area, points, stroke, fillId };
  }, [days, isDark, width, height]);

  if (!svg) {
    return <span className={`text-[10px] ${isDark ? "text-slate-500" : "text-gray-400"}`}>—</span>;
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="shrink-0 overflow-visible"
      aria-hidden
    >
      <defs>
        <linearGradient id={svg.fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={svg.stroke} stopOpacity={0.35} />
          <stop offset="100%" stopColor={svg.stroke} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      <path d={svg.area} fill={`url(#${svg.fillId})`} />
      <path d={svg.line} fill="none" stroke={svg.stroke} strokeWidth={1.75} strokeLinecap="round" />
    </svg>
  );
};

const TlpMrDailyAreaChart = ({
  daily, totalNet, dailyAvgNet, isDark, textClass, labelClass,
}) => {
  const chart = useMemo(() => {
    const series = (daily || []).filter((d) => d?.date);
    if (!series.length) return null;

    const width = 760;
    const height = 220;
    const pad = { top: 16, right: 20, bottom: 36, left: 44 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const values = series.map((d) => Number(d.net_count) || 0);
    const maxV = tlpMrNiceMax(Math.max(...values, 0));
    const yTicks = [0, maxV * 0.33, maxV * 0.66, maxV].map((v) => Math.round(v));

    const points = values.map((val, idx) => {
      const x = pad.left + (values.length === 1 ? innerW / 2 : (idx / (values.length - 1)) * innerW);
      const y = pad.top + innerH - (maxV > 0 ? (val / maxV) * innerH : 0);
      return { x, y, val, date: series[idx].date };
    });

    const linePath = tlpBuildSmoothPath(points);
    const areaPath = `${linePath} L ${points[points.length - 1].x} ${pad.top + innerH} L ${points[0].x} ${pad.top + innerH} Z`;
    const labelStep = values.length > 20 ? 2 : 1;

    return {
      width,
      height,
      pad,
      innerH,
      maxV,
      yTicks,
      points,
      linePath,
      areaPath,
      labelStep,
      gridColor: isDark ? "rgba(148,163,184,0.15)" : "rgba(203,213,225,0.9)",
      axisColor: isDark ? "rgba(148,163,184,0.5)" : "rgba(100,116,139,0.7)",
    };
  }, [daily, isDark]);

  if (!chart) return null;

  const stroke = isDark ? "#a78bfa" : "#8b5cf6";
  const gradId = "tlp-mr-area-grad-main";

  return (
    <div className={`rounded-xl border overflow-hidden ${isDark ? "border-violet-500/20 bg-slate-950/40" : "border-violet-100 bg-white shadow-sm"}`}>
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-2 rounded-full shrink-0 ${isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-600"}`}>
            <Activity className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h4 className={`text-sm font-semibold ${textClass}`}>Günlük MR Gönderimi</h4>
            <p className={`text-[11px] mt-0.5 ${labelClass}`}>
              Toplam {totalNet ?? "—"}
              {dailyAvgNet != null ? ` · Günlük ortalama ${dailyAvgNet}` : ""}
            </p>
          </div>
        </div>
        <span className={`text-[10px] font-medium px-2 py-1 rounded-md shrink-0 ${isDark ? "bg-slate-800 text-slate-300" : "bg-gray-100 text-gray-600"}`}>
          Günlük
        </span>
      </div>
      <div className="px-2 pb-3">
        <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="w-full h-auto" role="img" aria-label="Günlük MR gönderim grafiği">
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0.03} />
            </linearGradient>
          </defs>
          {chart.yTicks.map((tick) => {
            const y = chart.pad.top + chart.innerH - (chart.maxV > 0 ? (tick / chart.maxV) * chart.innerH : 0);
            return (
              <g key={tick}>
                <line x1={chart.pad.left} y1={y} x2={chart.width - chart.pad.right} y2={y} stroke={chart.gridColor} strokeDasharray="4 4" />
                <text x={chart.pad.left - 8} y={y + 4} textAnchor="end" fontSize={10} fill={chart.axisColor}>
                  {tick}
                </text>
              </g>
            );
          })}
          {chart.points.map((p, idx) => (
            idx % chart.labelStep === 0 || idx === chart.points.length - 1 ? (
              <text
                key={p.date}
                x={p.x}
                y={chart.height - 10}
                textAnchor="middle"
                fontSize={9}
                fill={chart.axisColor}
              >
                {formatMrAxisDay(p.date)}
              </text>
            ) : null
          ))}
          <path d={chart.areaPath} fill={`url(#${gradId})`} />
          <path d={chart.linePath} fill="none" stroke={stroke} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
          {chart.points.map((p) => (
            <circle key={p.date} cx={p.x} cy={p.y} r={3.5} fill={isDark ? "#1e1b4b" : "#fff"} stroke={stroke} strokeWidth={2} />
          ))}
        </svg>
      </div>
    </div>
  );
};

const TlpBarMeter = ({ value, max, barClass, isDark, className = "" }) => {
  const v = Math.max(0, Number(value) || 0);
  const m = Math.max(v, Number(max) || 0, 1);
  const pct = Math.min(100, (v / m) * 100);
  return (
    <div className={`rounded-full overflow-hidden h-1.5 ${isDark ? "bg-slate-700/70" : "bg-gray-200"} ${className}`}>
      <div className={`h-full rounded-full transition-all duration-500 ${barClass}`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const TlpKpiCard = ({ icon: Icon, label, value, hint, accentKey, isDark, textClass, labelClass }) => {
  const acc = tlpAccent(accentKey, isDark);
  return (
    <div
      className={`rounded-xl border p-3 flex flex-col gap-2 min-h-[88px] ${
        isDark ? `bg-slate-900/60 ${acc.border}` : `bg-white ${acc.border} shadow-sm`
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`p-1.5 rounded-lg ${acc.chip}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className={`text-lg font-bold tabular-nums leading-tight ${textClass}`}>{value ?? "—"}</span>
      </div>
      <div>
        <p className={`text-[11px] font-medium ${textClass}`}>{label}</p>
        {hint ? <p className={`text-[10px] mt-0.5 ${labelClass}`}>{hint}</p> : null}
      </div>
    </div>
  );
};

const TlpModuleNav = ({ activeId, onSelect, isDark, labelClass }) => (
  <nav
    className={`flex flex-wrap gap-1.5 p-1.5 rounded-xl border ${
      isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-200 bg-gray-50/90"
    }`}
    aria-label="Modül gezintisi"
  >
    {TLP_MODULE_NAV.map(({ id, label, icon: Icon }) => {
      const on = activeId === id;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onSelect(id)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
            on
              ? (isDark ? "bg-emerald-600/25 text-emerald-200 ring-1 ring-emerald-500/40" : "bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200")
              : (isDark ? "text-slate-400 hover:bg-slate-800/80 hover:text-slate-200" : "text-gray-600 hover:bg-white hover:text-gray-900")
          }`}
        >
          <Icon className="w-3.5 h-3.5 shrink-0 opacity-80" />
          {label}
        </button>
      );
    })}
  </nav>
);

const formatEmploymentDate = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value).slice(0, 10);
  if (s === "0000-00-00" || s.startsWith("0000-00-00")) return "";
  return s;
};

const ciroRowLeftClass = (isDark) => (isDark ? "text-rose-400 font-medium" : "text-rose-600 font-medium");

const formatTlpDisplayDate = (value) => {
  const s = formatEmploymentDate(value);
  if (!s) return "";
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
};

const KALITELI_BATCH_LATEST = "latest";

const formatReportDate = (value) => {
  const s = formatEmploymentDate(value);
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
};

const formatKaliteliBatchOption = (b) => {
  if (!b) return "";
  const ps = formatReportDate(b.period_start);
  const pe = formatReportDate(b.period_end);
  const range = ps && pe ? `${ps} – ${pe}` : "";
  return `${b.sheet_title || "Sheet"}${range ? ` · ${range}` : ""}`;
};

const formatKaliteliPeriodLabel = (start, end, sheetTitle) => {
  const ps = formatTlpDisplayDate(start);
  const pe = formatTlpDisplayDate(end);
  if (ps && pe) {
    return ps === pe ? ps : `${ps} – ${pe}`;
  }
  if (sheetTitle) return String(sheetTitle);
  return "—";
};

/** Özel Asistan Raporu 2 — TL × Kalite ile aynı 5 kriter */
const TLP_TL_KALITE_CRITERIA = [
  { key: "ordeleme", label: "İrdeleme" },
  { key: "mr_isteme", label: "MR İsteme" },
  { key: "mr_tepinme", label: "MR Tepinme" },
  { key: "tedirginlik", label: "Tedirginlik" },
  { key: "uzmanlik", label: "Uzmanlık" },
];

const tlpYesNoLabel = (v) => {
  if (v === "evet") return "Evet";
  if (v === "hayir") return "Hayır";
  return "—";
};

const TLP_STATE_KEY_PREFIX = "teamLeaderPerformanceState_v3";

const tlpStorageKey = (userKey) => `${TLP_STATE_KEY_PREFIX}_u${userKey || "0"}`;

const isValidTlpBundle = (b) => {
  if (!b || typeof b !== "object") return false;
  return b.modules != null && typeof b.modules === "object";
};

/** Yalnızca filtreler (sorgu sonucu / bundle önbelleklenmez). */
const loadTlpCachedFilters = (userKey) => {
  try {
    const raw = sessionStorage.getItem(tlpStorageKey(userKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      companyId: parsed.companyId != null ? String(parsed.companyId) : "",
      leaderId: parsed.leaderId != null ? String(parsed.leaderId) : "",
      leaderIds: Array.isArray(parsed.leaderIds)
        ? parsed.leaderIds.map(String)
        : (parsed.leaderId ? [String(parsed.leaderId)] : []),
      startDate: parsed.startDate != null ? String(parsed.startDate) : "",
      endDate: parsed.endDate != null ? String(parsed.endDate) : "",
      kaliteliBatchId: parsed.kaliteliBatchId ?? null,
      datePreset: parsed.datePreset != null ? String(parsed.datePreset) : "",
    };
  } catch {
    return null;
  }
};

const saveTlpCachedFilters = (userKey, payload) => {
  try {
    sessionStorage.setItem(tlpStorageKey(userKey), JSON.stringify({
      companyId: payload.companyId ?? "",
      leaderId: payload.leaderId ?? "",
      leaderIds: Array.isArray(payload.leaderIds) ? payload.leaderIds : [],
      startDate: payload.startDate ?? "",
      endDate: payload.endDate ?? "",
      kaliteliBatchId: payload.kaliteliBatchId ?? null,
      datePreset: payload.datePreset ?? "",
      cachedAt: Date.now(),
    }));
  } catch {
    /* quota / private mode */
  }
};

const clearTlpCachedState = (userKey) => {
  try {
    sessionStorage.removeItem(tlpStorageKey(userKey));
  } catch {
    /* ignore */
  }
};

const TlpKaliteCriteriaGrid = ({ criteria, isDark, labelClass }) => {
  const rows = (criteria?.length ? criteria : TLP_TL_KALITE_CRITERIA.map(({ key, label }) => ({
    key, label, tl: null, kalite: null, match: false, mismatch: false,
  })));

  return (
    <div className="mt-2 pl-1">
      <div className={`grid grid-cols-[1fr_auto_1fr] gap-x-2 gap-y-1 text-[9px] font-semibold uppercase tracking-wide mb-1.5 px-1 ${labelClass}`}>
        <span>Tk. Lideri</span>
        <span className="text-center">Kriter</span>
        <span className="text-right">Kalite</span>
      </div>
      <div className="space-y-1">
        {rows.map((c) => {
          const hasBoth = c.tl != null && c.kalite != null;
          const rowBg = hasBoth && c.mismatch
            ? (isDark ? "bg-rose-500/10 ring-1 ring-rose-500/25" : "bg-rose-50 ring-1 ring-rose-200")
            : hasBoth && c.match
              ? (isDark ? "bg-emerald-500/8 ring-1 ring-emerald-500/20" : "bg-emerald-50/90 ring-1 ring-emerald-200/80")
              : (isDark ? "bg-slate-800/40" : "bg-white/80 ring-1 ring-gray-100");
          return (
            <div
              key={c.key}
              className={`grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg px-2 py-1 text-[11px] ${rowBg}`}
            >
              <span className={isDark ? "text-slate-200 font-medium" : "text-gray-800 font-medium"}>
                {tlpYesNoLabel(c.tl)}
              </span>
              <span className={`text-[10px] text-center px-1 ${labelClass}`}>{c.label}</span>
              <span className={`text-right font-medium ${isDark ? "text-slate-200" : "text-gray-800"}`}>
                {tlpYesNoLabel(c.kalite)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ModuleCard = ({ title, icon: Icon, children, isDark, hint, accent = "emerald", sectionId }) => {
  const acc = tlpAccent(accent, isDark);
  return (
    <section
      id={sectionId}
      className={`rounded-2xl border p-4 md:p-5 scroll-mt-24 transition-shadow ${
        isDark ? `border-slate-800 bg-slate-900/50 ${acc.border}` : `border-gray-200 bg-white shadow-sm ${acc.border}`
      }`}
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`p-2 rounded-xl ${acc.chip}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <h3 className={`text-sm font-semibold ${isDark ? "text-slate-100" : "text-gray-900"}`}>{title}</h3>
          {hint ? <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{hint}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
};

const TeamLeaderPerformanceReportPage = ({
  apiBase, isDark, textClass, labelClass, isSuperAdmin, userCompanyId, companies = [],
  cacheUserKey = "0",
  onOpenMrMapping, onOpenMrRecords, onOpenCiroRecords,
}) => {
  const spnDefaultCompanyId = useMemo(() => {
    const spn = (companies || []).find((c) => String(c.kod || "").toLowerCase() === "spn");
    return spn ? String(spn.id) : ((companies || [])[0]?.id ? String(companies[0].id) : "");
  }, [companies]);

  const defaultCompanyId = isSuperAdmin ? spnDefaultCompanyId : String(userCompanyId || "");
  const initialFilters = useMemo(() => loadTlpCachedFilters(cacheUserKey), [cacheUserKey]);

  const [leaders, setLeaders] = useState([]);
  const [selectedLeaderIds, setSelectedLeaderIds] = useState(() => {
    if (initialFilters?.leaderIds?.length) return initialFilters.leaderIds.map(String);
    if (initialFilters?.leaderId) return [String(initialFilters.leaderId)];
    return [];
  });
  const [leaderPickerOpen, setLeaderPickerOpen] = useState(false);
  const leaderPickerRef = useRef(null);
  const [compareBundles, setCompareBundles] = useState(null);
  const primaryLeaderId = selectedLeaderIds[0] ?? "";
  const isCompareMode = (compareBundles?.length ?? 0) >= 2;
  const [companyId, setCompanyId] = useState(() => {
    if (initialFilters?.companyId) return String(initialFilters.companyId);
    return defaultCompanyId;
  });
  const [startDate, setStartDate] = useState(() => initialFilters?.startDate ?? "");
  const [endDate, setEndDate] = useState(() => initialFilters?.endDate ?? "");
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(-1);
  const loadingStepTimerRef = useRef(null);
  const [bundle, setBundle] = useState(null);
  const [importing, setImporting] = useState(false);
  const [mrSyncing, setMrSyncing] = useState(false);
  const [mrSyncMode, setMrSyncMode] = useState(null);
  const [mrSyncInfo, setMrSyncInfo] = useState(null);
  const [mrSource, setMrSource] = useState("firebase");
  const [mrModalOpen, setMrModalOpen] = useState(false);
  const [mrParsed, setMrParsed] = useState(null);
  const [mrFileName, setMrFileName] = useState("");
  const [mrDefaultYear, setMrDefaultYear] = useState(new Date().getFullYear());
  const [mrImporting, setMrImporting] = useState(false);
  const [mrImportResult, setMrImportResult] = useState(null);
  const [mrParsing, setMrParsing] = useState(false);
  const [companyAgents, setCompanyAgents] = useState([]);
  const ciroInputRef = useRef(null);
  const mrInputRef = useRef(null);
  const [kaliteliTeamOpen, setKaliteliTeamOpen] = useState(() => new Set());
  const [kaliteliBatchId, setKaliteliBatchId] = useState(() => initialFilters?.kaliteliBatchId ?? null);
  const [tlKaliteExpanded, setTlKaliteExpanded] = useState(() => new Set());
  const [kaliteliRefreshing, setKaliteliRefreshing] = useState(false);
  /** ciro: büyükten küçüğe desc, küçükten büyüğe asc */
  const [ciroSortOrder, setCiroSortOrder] = useState("desc");
  const [datePreset, setDatePreset] = useState(() => initialFilters?.datePreset ?? "");

  const effectiveCompanyId = useMemo(() => {
    if (isSuperAdmin) {
      const n = Number(companyId);
      return companyId && Number.isFinite(n) && n > 0 ? n : null;
    }
    const n = Number(userCompanyId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [isSuperAdmin, companyId, userCompanyId]);

  useEffect(() => {
    if (!isSuperAdmin || !spnDefaultCompanyId) return;
    if (initialFilters?.companyId) return;
    const n = Number(companyId);
    if (!companyId || !Number.isFinite(n) || n <= 0) {
      setCompanyId(String(spnDefaultCompanyId));
    }
  }, [isSuperAdmin, spnDefaultCompanyId, companyId, initialFilters?.companyId]);

  const persistFilters = useCallback((next) => {
    saveTlpCachedFilters(cacheUserKey, {
      companyId: next.companyId ?? companyId,
      leaderId: next.leaderId ?? primaryLeaderId,
      leaderIds: next.leaderIds ?? selectedLeaderIds,
      startDate: next.startDate ?? startDate,
      endDate: next.endDate ?? endDate,
      kaliteliBatchId: next.kaliteliBatchId !== undefined ? next.kaliteliBatchId : kaliteliBatchId,
      datePreset: next.datePreset !== undefined ? next.datePreset : datePreset,
    });
  }, [cacheUserKey, companyId, primaryLeaderId, selectedLeaderIds, startDate, endDate, kaliteliBatchId, datePreset]);

  const applyDatePreset = useCallback((presetId) => {
    setDatePreset(presetId);
    const range = resolveTlpDatePreset(presetId);
    if (range) {
      setStartDate(range.start);
      setEndDate(range.end);
      persistFilters({ startDate: range.start, endDate: range.end, datePreset: presetId });
    } else {
      persistFilters({ datePreset: presetId });
    }
  }, [persistFilters]);

  const loadMeta = useCallback(async () => {
    if (!effectiveCompanyId) return;
    try {
      const res = await axios.get(`${apiBase}/team-leader-performance`, {
        params: { action: "meta", company_id: effectiveCompanyId },
      });
      setLeaders(res.data?.leaders || []);
      setMrSyncInfo(res.data?.mr_sync || null);
      setMrSource(res.data?.mr_source === "excel" ? "excel" : "firebase");
      if (!selectedLeaderIds.length && !initialFilters?.leaderId && res.data?.leaders?.length === 1) {
        const only = String(res.data.leaders[0].id);
        setSelectedLeaderIds([only]);
        persistFilters({ leaderId: only, leaderIds: [only] });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lider listesi yüklenemedi");
    }
  }, [apiBase, effectiveCompanyId, selectedLeaderIds.length, initialFilters?.leaderId, persistFilters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    if (!leaderPickerOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (leaderPickerRef.current && !leaderPickerRef.current.contains(e.target)) {
        setLeaderPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [leaderPickerOpen]);

  useEffect(() => {
    if (!effectiveCompanyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${apiBase}/agents`, { timeout: 90000 });
        const raw = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        const list = raw.filter(
          (a) => Number(a.company_id) === Number(effectiveCompanyId),
        );
        if (!cancelled) setCompanyAgents(list);
      } catch {
        if (!cancelled) setCompanyAgents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, effectiveCompanyId]);

  const fetchLeaderBundleApi = useCallback(async (leaderUserId, batchOverride) => {
    if (!effectiveCompanyId || !leaderUserId || !startDate || !endDate) {
      return null;
    }
    const batchId = batchOverride !== undefined ? batchOverride : kaliteliBatchId;
    const params = {
      action: "bundle",
      company_id: effectiveCompanyId,
      leader_user_id: leaderUserId,
      start_date: startDate,
      end_date: endDate,
    };
    if (batchId) params.kaliteli_batch_id = batchId;
    const res = await axios.get(`${apiBase}/team-leader-performance`, {
      params,
      timeout: 120000,
    });
    if (!isValidTlpBundle(res.data)) {
      throw Object.assign(new Error("Geçersiz rapor yanıtı"), { response: { data: { detail: "Rapor yanıtı eksik (modules)" } } });
    }
    return res.data;
  }, [apiBase, effectiveCompanyId, startDate, endDate, kaliteliBatchId]);

  const fetchBundle = useCallback(async (batchOverride, { silent = false } = {}) => {
    if (!effectiveCompanyId || !primaryLeaderId || !startDate || !endDate) {
      if (!silent) toast.warning("Firma, takım lideri ve tarih aralığı seçin");
      return null;
    }
    const batchId = batchOverride !== undefined ? batchOverride : kaliteliBatchId;
    const data = await fetchLeaderBundleApi(primaryLeaderId, batchOverride);
    if (!data) return null;
    setBundle(data);
    saveTlpCachedFilters(cacheUserKey, {
      companyId: String(effectiveCompanyId),
      leaderId: primaryLeaderId,
      leaderIds: selectedLeaderIds,
      startDate,
      endDate,
      kaliteliBatchId: batchId || null,
    });
    return data;
  }, [apiBase, effectiveCompanyId, primaryLeaderId, selectedLeaderIds, startDate, endDate, kaliteliBatchId, cacheUserKey, fetchLeaderBundleApi]);

  const clearLoadingStepTimer = useCallback(() => {
    if (loadingStepTimerRef.current) {
      clearInterval(loadingStepTimerRef.current);
      loadingStepTimerRef.current = null;
    }
  }, []);

  const startLoadingStepAnimation = useCallback(() => {
    clearLoadingStepTimer();
    setLoadingStep(0);
    let step = 0;
    loadingStepTimerRef.current = setInterval(() => {
      step = Math.min(step + 1, TLP_LOADING_STEPS.length - 2);
      setLoadingStep(step);
    }, 520);
  }, [clearLoadingStepTimer]);

  useEffect(() => () => clearLoadingStepTimer(), [clearLoadingStepTimer]);

  const runQuery = useCallback(async () => {
    if (!selectedLeaderIds.length) {
      toast.warning("En az bir takım lideri seçin");
      return;
    }
    if (!startDate || !endDate) {
      toast.warning("Tarih aralığı seçin");
      return;
    }
    setLoading(true);
    startLoadingStepAnimation();
    try {
      if (selectedLeaderIds.length === 1) {
        setCompareBundles(null);
        await fetchBundle(undefined, { silent: false });
      } else {
        setBundle(null);
        const results = await Promise.all(
          selectedLeaderIds.map(async (lid) => {
            try {
              const b = await fetchLeaderBundleApi(lid);
              if (!b) return null;
              const leaderName = leaders.find((l) => String(l.id) === String(lid))?.name
                || b.leader?.name
                || `Lider #${lid}`;
              return { leaderId: String(lid), leaderName, bundle: b };
            } catch {
              return null;
            }
          }),
        );
        const ok = results.filter(Boolean);
        if (ok.length < 2) {
          toast.error("Kıyaslama için en az 2 liderin raporu yüklenmeli");
          setCompareBundles(null);
        } else {
          setCompareBundles(ok);
          saveTlpCachedFilters(cacheUserKey, {
            companyId: String(effectiveCompanyId),
            leaderId: primaryLeaderId,
            leaderIds: selectedLeaderIds,
            startDate,
            endDate,
            kaliteliBatchId: kaliteliBatchId || null,
          });
        }
      }
      setLoadingStep(TLP_LOADING_STEPS.length - 1);
      await new Promise((r) => setTimeout(r, 320));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rapor yüklenemedi");
      setBundle(null);
      setCompareBundles(null);
    } finally {
      clearLoadingStepTimer();
      setLoading(false);
      setLoadingStep(-1);
    }
  }, [
    selectedLeaderIds,
    startDate,
    endDate,
    fetchBundle,
    fetchLeaderBundleApi,
    leaders,
    effectiveCompanyId,
    primaryLeaderId,
    cacheUserKey,
    kaliteliBatchId,
    startLoadingStepAnimation,
    clearLoadingStepTimer,
  ]);

  const onKaliteliBatchChange = useCallback(async (value) => {
    const nextId = value === KALITELI_BATCH_LATEST ? null : Number(value);
    setKaliteliBatchId(nextId);
    persistFilters({ kaliteliBatchId: nextId });
    if (!bundle || !primaryLeaderId || !startDate || !endDate) {
      return;
    }
    setKaliteliRefreshing(true);
    try {
      await fetchBundle(nextId, { silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaliteli dönem yüklenemedi");
    } finally {
      setKaliteliRefreshing(false);
    }
  }, [bundle, primaryLeaderId, startDate, endDate, fetchBundle, persistFilters]);

  const clearFilters = useCallback(() => {
    clearTlpCachedState(cacheUserKey);
    setSelectedLeaderIds([]);
    setStartDate("");
    setEndDate("");
    setBundle(null);
    setCompareBundles(null);
    setKaliteliBatchId(null);
    setDatePreset("");
    setCompanyId(defaultCompanyId);
    setLeaderPickerOpen(false);
  }, [cacheUserKey, defaultCompanyId]);

  const onCiroFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !effectiveCompanyId) return;
    setImporting(true);
    try {
      const buf = await file.arrayBuffer();
      const parsed = parseTlpCiroWorkbook(buf);
      if (parsed.error) {
        toast.error(parsed.error);
        return;
      }
      if (!parsed.rows.length) {
        toast.warning("Eşleşen satır yok");
        return;
      }
      const res = await axios.post(
        `${apiBase}/team-leader-performance?import=ciro`,
        {
          company_id: effectiveCompanyId,
          source_file: file.name,
          period_start: parsed.periodStart || startDate || null,
          period_end: parsed.periodEnd || endDate || null,
          rows: parsed.rows,
        },
      );
      const ins = res.data?.inserted ?? parsed.rows.length;
      const upd = res.data?.updated ?? 0;
      const fmtLabel = parsed.format === "assistant_randevu_list"
        ? "randevu listesi"
        : (parsed.format === "assistant_prim_table" ? "prim tablosu" : "günlük asistan");
      toast.success(
        `Ciro: ${ins} yeni${upd ? `, ${upd} güncellendi` : ""} (${fmtLabel})${onOpenCiroRecords ? " — Ciro Kayıtları sayfasından kontrol edin" : ""}`,
      );
      if (selectedLeaderIds.length && startDate && endDate) runQuery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ciro import başarısız");
    } finally {
      setImporting(false);
    }
  };

  const onMrExcelFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!effectiveCompanyId) {
      toast.warning(isSuperAdmin ? "Önce üstten firma seçin (içeri aktarma için gerekli)." : "Firma bilgisi eksik; yöneticinize başvurun.");
    }

    setMrFileName(file.name);
    setMrImportResult(null);
    setMrParsed(null);
    setMrModalOpen(true);
    setMrParsing(true);

    try {
      const buf = await file.arrayBuffer();
      const year = endDate && /^\d{4}/.test(endDate)
        ? parseInt(endDate.slice(0, 4), 10)
        : new Date().getFullYear();
      await new Promise((r) => setTimeout(r, 0));
      const parsed = parseMrBtWorkbook(buf, { defaultYear: Number.isFinite(year) ? year : new Date().getFullYear() });
      setMrDefaultYear(Number.isFinite(year) ? year : new Date().getFullYear());
      setMrParsed(parsed);
      if (parsed.error) {
        toast.warning(parsed.error);
      } else if (!parsed.blocks?.length) {
        toast.warning("Excel okundu ancak MR tablo başlığı bulunamadı.");
      }
    } catch (err) {
      console.error("MR Excel parse", err);
      setMrParsed({ error: err?.message || "Excel okunamadı", blocks: [], allRecords: [] });
      toast.error(err?.message || "Excel okunamadı");
    } finally {
      setMrParsing(false);
    }
  };

  const closeMrModal = () => {
    if (mrImporting) return;
    setMrModalOpen(false);
    setMrParsed(null);
    setMrImportResult(null);
  };

  const confirmMrImport = async (payload) => {
    if (!effectiveCompanyId) {
      toast.warning(isSuperAdmin ? "İçeri aktarmak için firma seçin." : "Firma bilgisi eksik.");
      return;
    }
    setMrImporting(true);
    try {
      const res = await axios.post(
        `${apiBase}/team-leader-performance?import=mr_daily`,
        { company_id: effectiveCompanyId, ...payload },
        { timeout: 120000 },
      );
      setMrImportResult(res.data);
      const ins = res.data?.inserted ?? 0;
      const upd = res.data?.updated ?? 0;
      const same = res.data?.unchanged ?? 0;
      const saved = ins + upd;
      if (saved > 0) {
        const parts = [];
        if (ins > 0) parts.push(`${ins} yeni`);
        if (upd > 0) parts.push(`${upd} güncellendi`);
        if (same > 0) parts.push(`${same} aynı`);
        toast.success(
          `MR: ${parts.join(", ")}${onOpenMrRecords ? " — MR Kayıtları sayfasından kontrol edin" : ""}`,
        );
      } else if (same > 0) {
        toast.info(`Tüm kayıtlar zaten güncel (${same} satır)`);
      } else if ((res.data?.unmatched?.length ?? 0) > 0) {
        toast.warning(`Kayıt yok — ${res.data.skipped ?? 0} atlandı, eşleşmeyen agent var`);
      } else {
        toast.warning("Kayıt yok — satırlar atlandı (tarih/agent kontrol edin)");
      }
      if (selectedLeaderIds.length && startDate && endDate) runQuery();
    } catch (err) {
      toast.error(err.response?.data?.detail || "MR import başarısız");
    } finally {
      setMrImporting(false);
    }
  };

  const runMrSync = async (mode) => {
    if (mrSource === "excel") {
      toast.info("Firebase MR kapalı. «MR Excel» ile yükleyin.");
      return;
    }
    if (!effectiveCompanyId) {
      toast.warning("Firma seçin");
      return;
    }
    if (mode === "full" && !window.confirm("Hisar MR tüm arşiv günleri indirilecek; birkaç dakika sürebilir. Devam?")) {
      return;
    }
    if (mode === "range" && (!startDate || !endDate)) {
      toast.warning("Aralık senkron için tarih seçin veya «Günlük güncelle» kullanın");
      return;
    }
    setMrSyncing(true);
    setMrSyncMode(mode);
    try {
      const body = { company_id: effectiveCompanyId, mode };
      if (mode === "range") {
        body.start_date = startDate;
        body.end_date = endDate;
      }
      if (mode === "daily") {
        body.lookback_days = 3;
      }
      const res = await axios.post(`${apiBase}/team-leader-performance?mr_sync=1`, body, {
        timeout: mode === "full" ? 600000 : 120000,
      });
      if (res.data?.ok) {
        toast.success(res.data.message || `MR: ${res.data.synced ?? 0} kayıt`);
        loadMeta();
        if (selectedLeaderIds.length && startDate && endDate) runQuery();
      } else {
        toast.warning(res.data?.message || "MR senkron tamamlanamadı");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "MR senkron hatası");
    } finally {
      setMrSyncing(false);
      setMrSyncMode(null);
    }
  };

  const modules = bundle?.modules && typeof bundle.modules === "object" ? bundle.modules : {};
  const inputClass = isDark ? "bg-slate-950 border-slate-700 text-white" : "bg-white border-gray-200";

  const toggleKaliteliTeam = useCallback((teamId) => {
    setKaliteliTeamOpen((prev) => {
      const next = new Set(prev);
      const key = String(teamId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    const teams = bundle?.modules?.kaliteli_conversion?.teams || [];
    if (!teams.length) {
      setKaliteliTeamOpen(new Set());
      return;
    }
    setKaliteliTeamOpen(new Set(teams.map((t) => String(t.team_id))));
  }, [bundle]);

  const leaderLabel = useMemo(() => {
    const l = leaders.find((x) => String(x.id) === String(primaryLeaderId));
    return l?.name || bundle?.leader?.name || "—";
  }, [leaders, primaryLeaderId, bundle]);

  const leaderPickerLabel = useMemo(() => {
    if (!selectedLeaderIds.length) return "Takım lideri seçin";
    if (selectedLeaderIds.length === 1) {
      const l = leaders.find((x) => String(x.id) === selectedLeaderIds[0]);
      return l?.name || "1 lider";
    }
    return `${selectedLeaderIds.length} lider · Kıyaslama`;
  }, [selectedLeaderIds, leaders]);

  const toggleLeaderSelection = useCallback((id) => {
    const key = String(id);
    setSelectedLeaderIds((prev) => {
      const has = prev.includes(key);
      const next = has ? prev.filter((x) => x !== key) : [...prev, key];
      persistFilters({ leaderIds: next, leaderId: next[0] ?? "" });
      setBundle(null);
      setCompareBundles(null);
      return next;
    });
  }, [persistFilters]);

  const tlpOverviewRef = useRef(null);
  const [tlpNavActive, setTlpNavActive] = useState("overview");

  const tlpOverview = useMemo(() => {
    const ciro = modules.ciro?.totals;
    const alignWithPct = (modules.tl_kalite_alignment?.agents || []).filter((a) => a.alignment?.percent != null);
    const kaliteAvg = alignWithPct.length
      ? Math.round(alignWithPct.reduce((s, a) => s + a.alignment.percent, 0) / alignWithPct.length)
      : null;
    const kaliteMismatch = (modules.tl_kalite_alignment?.agents || []).reduce((n, a) => {
      const diffLen = a.alignment?.diffs?.length;
      if (diffLen != null && diffLen > 0) return n + diffLen;
      return n + (a.alignment?.criteria || []).filter((c) => c.mismatch).length;
    }, 0);
    const teams = modules.kaliteli_conversion?.teams || [];
    const wpTeams = teams.filter((t) => t.wp_conversion_avg != null);
    const disTeams = teams.filter((t) => t.dis_arama_conversion_avg != null);
    const wpAvg = wpTeams.length
      ? Math.round(wpTeams.reduce((s, t) => s + t.wp_conversion_avg, 0) / wpTeams.length)
      : null;
    const disAvg = disTeams.length
      ? Math.round(disTeams.reduce((s, t) => s + t.dis_arama_conversion_avg, 0) / disTeams.length)
      : null;
    const ciroRows = modules.ciro?.rows || [];
    const maxIslem = ciroRows.reduce((m, r) => Math.max(m, Number(r.islem_adeti) || 0), 0);
    const topCiroAgents = [...ciroRows]
      .sort((a, b) => (Number(b.islem_adeti) || 0) - (Number(a.islem_adeti) || 0))
      .slice(0, 5);
    const callAgents = modules.call_metrics?.agents || [];
    const maxTalk = callAgents.reduce((m, a) => Math.max(m, Number(a.avg_daily_talk_seconds) || 0), 0);
    const mrDaily = modules.mr?.daily || [];
    const maxMrDay = mrDaily.reduce((m, d) => Math.max(m, Number(d.net_count) || 0), 0);
    return {
      ciro,
      kaliteAvg,
      kaliteMismatch,
      wpAvg,
      disAvg,
      maxIslem,
      topCiroAgents,
      maxTalk,
      mrDaily,
      maxMrDay,
      mrTotal: modules.mr?.total_net,
      mrDailyAvg: modules.mr?.daily_avg_net,
      talkTeam: modules.call_metrics?.team_avg_daily_talk_seconds,
      breakTeam: modules.call_metrics?.team_avg_daily_break_seconds,
      personnel: modules.personnel?.summary,
    };
  }, [modules]);

  const scrollTlpSection = useCallback((id) => {
    setTlpNavActive(id);
    if (id === "overview") {
      tlpOverviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const sortedCiroRows = useMemo(() => {
    const rows = [...(modules.ciro?.rows || [])];
    const dir = ciroSortOrder === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      const za = a.roster_zero_fill ? 1 : 0;
      const zb = b.roster_zero_fill ? 1 : 0;
      if (za !== zb) return za - zb;
      const ca = Number(a.toplam_ciro) || 0;
      const cb = Number(b.toplam_ciro) || 0;
      if (ca !== cb) return (ca - cb) * dir;
      return String(a.assistant_name || "").localeCompare(String(b.assistant_name || ""), "tr");
    });
    return rows;
  }, [modules.ciro?.rows, ciroSortOrder]);

  const toggleCiroSort = useCallback(() => {
    setCiroSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  }, []);

  const sortedPersonnelAgents = useMemo(() => {
    const list = [...(modules.personnel?.agents || [])];
    const isActiveAgent = (a) => a.is_active !== false && !a.has_left;
    list.sort((a, b) => {
      const aActive = isActiveAgent(a);
      const bActive = isActiveAgent(b);
      if (aActive !== bActive) return aActive ? -1 : 1;
      if (aActive && bActive) {
        const da = Number(a.tenure_days) || 0;
        const db = Number(b.tenure_days) || 0;
        if (db !== da) return db - da;
      } else {
        const da = Number(a.tenure_until_exit_days ?? a.tenure_days) || 0;
        const db = Number(b.tenure_until_exit_days ?? b.tenure_days) || 0;
        if (da !== db) return da - db;
      }
      return String(a.agent_name || "").localeCompare(String(b.agent_name || ""), "tr");
    });
    return list;
  }, [modules.personnel?.agents]);

  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border overflow-visible ${isDark ? "border-slate-800 bg-slate-900/60" : "border-gray-200 bg-white"}`}>
        <div className={`px-4 py-4 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isDark ? "bg-emerald-500/15 text-emerald-400" : "bg-emerald-50 text-emerald-700"}`}>
              <BarChart3 className="w-5 h-5" />
            </div>
            <div>
              <h2 className={`text-lg font-semibold ${textClass}`}>Takım Lideri Performansları</h2>
              <p className={`text-xs ${labelClass}`}>Ciro · MR · Kaliteli dönüşüm · Görüşme/mola · TL×Kalite · Personel (6 modül)</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <input ref={ciroInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onCiroFile} disabled={importing} />
            <Button type="button" size="sm" variant="outline" className={isDark ? "border-slate-700" : ""} disabled={importing} onClick={() => ciroInputRef.current?.click()}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Ciro Excel
            </Button>
            {onOpenCiroRecords ? (
              <Button type="button" size="sm" variant="outline" className={isDark ? "border-emerald-700 text-emerald-200" : "border-emerald-200 text-emerald-800"} onClick={onOpenCiroRecords}>
                <Wallet className="w-4 h-4 mr-1" />
                Ciro Kayıtları
              </Button>
            ) : null}
            <input
              ref={mrInputRef}
              id="tlp-mr-excel-input"
              type="file"
              accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              className="sr-only"
              onChange={onMrExcelFile}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={isDark ? "border-violet-800 text-violet-200" : "border-violet-200 text-violet-800"}
              disabled={mrParsing}
              onClick={() => {
                const el = mrInputRef.current;
                if (el) {
                  el.value = "";
                  el.click();
                } else {
                  toast.error("Dosya seçici açılamadı; sayfayı yenileyin.");
                }
              }}
            >
              {mrParsing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              MR Excel
            </Button>
            {onOpenMrRecords ? (
              <Button type="button" size="sm" variant="outline" className={isDark ? "border-violet-700 text-violet-200" : "border-violet-200 text-violet-800"} onClick={onOpenMrRecords}>
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                MR Kayıtları
              </Button>
            ) : null}
            {mrSource !== "excel" && (
              <>
                <Button type="button" size="sm" variant="outline" onClick={() => runMrSync("full")} disabled={mrSyncing} className={isDark ? "border-slate-700" : ""}>
                  {mrSyncing && mrSyncMode === "full" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  MR tüm arşiv
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => runMrSync("daily")} disabled={mrSyncing} className={isDark ? "border-slate-700" : ""}>
                  {mrSyncing && mrSyncMode === "daily" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  MR günlük
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => runMrSync("range")} disabled={mrSyncing} className={isDark ? "text-slate-400" : ""} title="Seçili tarih aralığı (max 31 gün)">
                  MR aralık
                </Button>
              </>
            )}
          </div>
        </div>
        <p className={`px-4 pb-2 text-[10px] ${isDark ? "text-emerald-300/90" : "text-emerald-800"}`}>
          Ciro Excel: «Günlük Asistan Detay», «Asistan Randevu Listesi» (Yapılan Tedavi + Alınan Ücret → gün/asistan toplamı) veya «Asistan Prim Tablosu». Aynı agent + gün tekrar yüklenirse güncellenir.
        </p>
        {mrSource === "excel" && (
          <p className={`px-4 pb-2 text-[10px] ${isDark ? "text-violet-300" : "text-violet-700"}`}>
            MR: Excel → ad soyada göre agent eşleşir → <code className="opacity-90">tl_mr_assistant_daily</code> (Firebase yok).
            «MR Excel» ile yükleyin; eklenen satırlar «MR Kayıtları» menüsünde listelenir.
          </p>
        )}
        {mrSource !== "excel" && mrSyncInfo?.state && (
          <p className={`px-4 pb-2 text-[10px] ${labelClass}`}>
            MR arşiv: {mrSyncInfo.state.full_backfill_at ? `tam çekim ${new Date(mrSyncInfo.state.full_backfill_at).toLocaleString("tr-TR")}` : "henüz tam arşiv yok"}
            {mrSyncInfo.state.last_daily_sync_at ? ` · son günlük ${new Date(mrSyncInfo.state.last_daily_sync_at).toLocaleString("tr-TR")}` : ""}
            {mrSyncInfo.state.last_archive_date ? ` · son gün ${mrSyncInfo.state.last_archive_date}` : ""}
          </p>
        )}
        <div className={`px-4 py-3 flex flex-wrap gap-2 items-end ${isDark ? "bg-slate-950/30" : "bg-gray-50/80"}`}>
          {isSuperAdmin && (
            <select
              value={companyId}
              onChange={(e) => {
                const v = e.target.value;
                setCompanyId(v);
                setSelectedLeaderIds([]);
                setLeaders([]);
                persistFilters({ companyId: v, leaderId: "", leaderIds: [] });
                setBundle(null);
                setCompareBundles(null);
              }}
              className={`h-9 rounded-md border px-2 text-sm min-w-[160px] ${inputClass}`}
            >
              <option value="">Firma seçin</option>
              {(companies || []).map((c) => <option key={c.id} value={c.id}>{c.ad || c.name || c.id}</option>)}
            </select>
          )}
          <div className="relative min-w-[200px]" ref={leaderPickerRef}>
            <button
              type="button"
              onClick={() => setLeaderPickerOpen((o) => !o)}
              className={`h-9 w-full rounded-md border px-2 text-sm text-left flex items-center justify-between gap-2 ${inputClass} ${
                selectedLeaderIds.length > 1 ? (isDark ? "border-emerald-600/50 ring-1 ring-emerald-500/30" : "border-emerald-300 ring-1 ring-emerald-200") : ""
              }`}
            >
              <span className="truncate flex items-center gap-1.5">
                {selectedLeaderIds.length > 1 ? <GitCompare className="w-3.5 h-3.5 shrink-0 text-emerald-500" /> : null}
                {leaderPickerLabel}
              </span>
              {leaderPickerOpen ? <ChevronUp className="w-3.5 h-3.5 shrink-0 opacity-60" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" />}
            </button>
            {leaderPickerOpen ? (
              <div
                className={`absolute z-[200] mt-1 w-full min-w-[240px] max-h-64 overflow-auto rounded-md border shadow-xl py-1 ${
                  isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-200"
                }`}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <p className={`px-3 py-1.5 text-[10px] ${labelClass}`}>
                  Tek lider: detaylı 6 modül · Birden fazla: modül modül kıyaslama
                </p>
                {leaders.length === 0 ? (
                  <p className={`px-3 py-2 text-xs ${labelClass}`}>
                    {effectiveCompanyId ? "Takım lideri bulunamadı." : "Önce firma seçin."}
                  </p>
                ) : null}
                {leaders.map((l) => {
                  const checked = selectedLeaderIds.includes(String(l.id));
                  return (
                    <label
                      key={l.id}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm ${
                        isDark ? "hover:bg-slate-800" : "hover:bg-gray-50"
                      } ${checked ? (isDark ? "bg-emerald-950/40" : "bg-emerald-50/80") : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-400 shrink-0"
                        checked={checked}
                        readOnly
                        onClick={(e) => {
                          e.preventDefault();
                          toggleLeaderSelection(l.id);
                        }}
                      />
                      <span className={textClass}>{l.name}</span>
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1 items-center">
            {TLP_DATE_PRESETS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => applyDatePreset(opt.id)}
                className={`h-9 px-2.5 rounded-md text-xs font-medium transition-colors ${
                  datePreset === opt.id
                    ? (isDark ? "bg-teal-600 text-white" : "bg-teal-600 text-white")
                    : (isDark ? "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-slate-200" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50")
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => {
              const v = e.target.value;
              setStartDate(v);
              setDatePreset("custom");
              persistFilters({ startDate: v, datePreset: "custom" });
            }}
            className={`h-9 w-36 ${inputClass}`}
          />
          <Input
            type="date"
            value={endDate}
            onChange={(e) => {
              const v = e.target.value;
              setEndDate(v);
              setDatePreset("custom");
              persistFilters({ endDate: v, datePreset: "custom" });
            }}
            className={`h-9 w-36 ${inputClass}`}
          />
          <Button type="button" size="sm" className="h-9" onClick={runQuery} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sorgula
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={`h-9 ${isDark ? "border-slate-700" : ""}`}
            onClick={clearFilters}
            disabled={loading}
            title="Kayıtlı filtreleri temizle"
          >
            <X className="w-4 h-4 mr-1" />
            Temizle
          </Button>
        </div>
      </div>

      {loading ? (
        <TlpQueryLoader
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          loadingStep={loadingStep}
          leaderLabel={leaderLabel}
        />
      ) : null}

      {!loading && isCompareMode && compareBundles ? (
        <TlpLeaderComparisonView
          compareEntries={compareBundles}
          startDate={startDate}
          endDate={endDate}
          isDark={isDark}
          textClass={textClass}
          labelClass={labelClass}
          formatDisplayDate={formatTlpDisplayDate}
        />
      ) : null}

      {!loading && !isCompareMode && bundle ? (
        <div className="space-y-4">
          <div
            ref={tlpOverviewRef}
            className={`rounded-2xl border overflow-hidden ${
              isDark
                ? "border-slate-800 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-emerald-950/30"
                : "border-gray-200 bg-gradient-to-br from-white via-emerald-50/40 to-teal-50/50 shadow-sm"
            }`}
          >
            <div className="px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-emerald-400/80" : "text-emerald-700"}`}>
                  Seçili dönem özeti
                </p>
                <h3 className={`text-lg font-bold ${textClass}`}>{leaderLabel}</h3>
                <p className={`text-xs mt-0.5 ${labelClass}`}>
                  {formatTlpDisplayDate(bundle.start_date)} — {formatTlpDisplayDate(bundle.end_date)}
                  {" · "}
                  {bundle.agent_count} asistan
                  {(bundle.teams || []).length ? ` · ${(bundle.teams || []).length} takım` : ""}
                </p>
              </div>
              <TlpModuleNav activeId={tlpNavActive} onSelect={scrollTlpSection} isDark={isDark} labelClass={labelClass} />
            </div>
            <div className="px-4 pb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
              <TlpKpiCard
                icon={TrendingUp}
                label="İşlem"
                value={tlpOverview.ciro?.islem ?? "—"}
                accentKey="emerald"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={Stethoscope}
                label="Muayene"
                value={tlpOverview.ciro?.muayene ?? "—"}
                accentKey="emerald"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={CircleX}
                label="İptal"
                value={tlpOverview.ciro?.iptal ?? "—"}
                accentKey="amber"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={Wallet}
                label="Ciro"
                value={formatCiroFull(tlpOverview.ciro?.ciro)}
                accentKey="emerald"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={Activity}
                label="MR (toplam)"
                value={tlpOverview.mrTotal ?? "—"}
                hint={tlpOverview.mrDailyAvg != null ? `Günlük ort. ${tlpOverview.mrDailyAvg}` : undefined}
                accentKey="violet"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={Scale}
                label="Kalite eşleşme"
                value={tlpOverview.kaliteAvg != null ? `%${tlpOverview.kaliteAvg}` : "—"}
                hint={tlpOverview.kaliteMismatch ? `${tlpOverview.kaliteMismatch} uyumsuz kriter` : "TL × Kalite ort."}
                accentKey="blue"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={Phone}
                label="WP dönüşüm"
                value={tlpOverview.wpAvg != null ? `%${tlpOverview.wpAvg}` : "—"}
                hint={tlpOverview.disAvg != null ? `Kaliteli işlem ort. %${tlpOverview.disAvg}` : undefined}
                accentKey="teal"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
            </div>
            {(tlpOverview.topCiroAgents.length > 0 || tlpOverview.mrDaily.length > 0) && (
              <div className={`mx-4 mb-4 grid gap-3 lg:grid-cols-2 rounded-xl border p-3 ${isDark ? "border-slate-800 bg-slate-950/35" : "border-gray-100 bg-white/70"}`}>
                {tlpOverview.topCiroAgents.length > 0 ? (
                  <div>
                    <p className={`text-[11px] font-semibold mb-2 ${textClass}`}>İşlem — öne çıkan asistanlar</p>
                    <div className="space-y-2">
                      {tlpOverview.topCiroAgents.map((r) => (
                        <div key={`${r.agent_id}-${r.segment_leader_user_id}`} className="grid grid-cols-[minmax(0,1fr)_2.5rem] gap-2 items-center text-[11px]">
                          <div className="min-w-0">
                            <div className={`truncate font-medium ${textClass}`}>{r.assistant_name}</div>
                            <TlpBarMeter
                              value={r.islem_adeti}
                              max={tlpOverview.maxIslem}
                              barClass={tlpAccent("emerald", isDark).bar}
                              isDark={isDark}
                              className="mt-1"
                            />
                          </div>
                          <span className={`tabular-nums text-right font-semibold ${textClass}`}>{r.islem_adeti}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {tlpOverview.mrDaily.length > 0 ? (
                  <div>
                    <p className={`text-[11px] font-semibold mb-2 ${textClass}`}>MR — günlük dağılım</p>
                    <div className="flex items-end gap-0.5 h-16">
                      {tlpOverview.mrDaily.slice(-14).map((d) => {
                        const barPx = tlpOverview.maxMrDay > 0
                          ? Math.max(4, (Number(d.net_count) / tlpOverview.maxMrDay) * 52)
                          : 4;
                        return (
                          <div key={d.date} className="flex-1 min-w-0 flex flex-col items-center justify-end gap-0.5 h-16" title={`${d.date}: ${d.net_count}`}>
                            <div
                              className={`w-full rounded-t ${tlpAccent("violet", isDark).bar} opacity-90`}
                              style={{ height: barPx }}
                            />
                            <span className={`text-[8px] truncate w-full text-center ${labelClass}`}>{String(d.date).slice(8)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <ModuleCard
            sectionId="tlp-mod-1"
            accent="emerald"
            title="1 — İşlem, muayene, iptal, ciro"
            icon={Wallet}
            isDark={isDark}
            hint={modules.ciro?.note || modules.ciro?.hint}
          >
            {!modules.ciro || modules.ciro.empty || !modules.ciro.totals ? (
              <p className={`text-sm ${labelClass}`}>{modules.ciro?.hint || "Ciro verisi yok — Sorgula ile yenileyin."}</p>
            ) : (
              <>
                {modules.ciro.adet_from_tutar_fallback ? (
                  <p className={`text-[11px] mb-2 rounded-md border px-2 py-1.5 ${isDark ? "border-amber-600/40 bg-amber-950/30 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                    {modules.ciro.note || "Adet alanları boş import edilmiş; tutarı olan günler geçici sayıldı. Tam adet için Asistan Tedavi Excel’ini tekrar yükleyin."}
                  </p>
                ) : null}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <TlpKpiCard icon={TrendingUp} label="İşlem" value={modules.ciro.totals?.islem} accentKey="emerald" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  <TlpKpiCard icon={Stethoscope} label="Muayene" value={modules.ciro.totals?.muayene} accentKey="emerald" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  <TlpKpiCard icon={CircleX} label="İptal" value={modules.ciro.totals?.iptal} accentKey="amber" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  <TlpKpiCard icon={Wallet} label="Ciro" value={formatCiroFull(modules.ciro.totals?.ciro)} accentKey="emerald" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                </div>
                <div className="max-h-64 overflow-auto text-xs">
                  <table className="w-full">
                    <thead>
                      <tr className={labelClass}>
                        <th className="text-left py-1">Asistan</th>
                        <th className="text-left py-1">Takım lideri</th>
                        <th className="text-right">İşlem</th>
                        <th className="text-right">Muayene</th>
                        <th className="text-right">İptal</th>
                        <th className="text-right">
                          <button
                            type="button"
                            className={`inline-flex items-center gap-0.5 font-semibold hover:opacity-80 ${isDark ? "text-slate-300" : "text-gray-600"}`}
                            onClick={toggleCiroSort}
                            title={ciroSortOrder === "desc" ? "Ciroya göre: büyükten küçüğe (tıkla: küçükten büyüğe)" : "Ciroya göre: küçükten büyüğe (tıkla: büyükten küçüğe)"}
                          >
                            Ciro
                            {ciroSortOrder === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                          </button>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCiroRows.map((r, i) => {
                        const left = r.has_left || Boolean(formatEmploymentDate(r.isten_cikis_tarihi));
                        const exitLabel = formatEmploymentDate(r.isten_cikis_tarihi);
                        const segStart = formatTlpDisplayDate(r.segment_period_start);
                        const segEnd = formatTlpDisplayDate(r.segment_period_end);
                        const segRange = segStart && segEnd
                          ? (segStart === segEnd ? segStart : `${segStart} – ${segEnd}`)
                          : "";
                        const leaderTitle = r.split_by_leader && segRange
                          ? `Bu satır: ${segRange} aralığındaki tedavi günleri (${r.segment_leader_name || "—"})`
                          : (r.segment_leader_name || undefined);
                        const isFormerLeaderSegment = Boolean(
                          r.split_by_leader
                          && r.segment_leader_name
                          && String(r.segment_leader_user_id ?? "") !== String(bundle?.leader?.id ?? primaryLeaderId ?? ""),
                        );
                        const departedRow = left && !r.roster_zero_fill;
                        return (
                        <tr
                          key={`${r.agent_id ?? "n"}-${r.segment_leader_user_id ?? "x"}-${r.segment_period_start ?? i}-${i}`}
                          className={
                            departedRow
                              ? isDark
                                ? "border-t border-rose-900/60 bg-rose-950/45"
                                : "border-t border-rose-200 bg-rose-50/90"
                              : r.roster_zero_fill
                                ? isDark
                                  ? "border-t border-slate-800 bg-slate-900/35 opacity-90"
                                  : "border-t border-gray-100 bg-gray-50/80"
                                : isDark
                                  ? "border-t border-slate-800"
                                  : "border-t border-gray-100"
                          }
                        >
                          <td
                            className={`py-1 ${departedRow ? ciroRowLeftClass(isDark) : textClass}`}
                            title={left && exitLabel ? `İşten çıkış: ${exitLabel}` : undefined}
                          >
                            {r.assistant_name}
                            {departedRow ? (
                              <Badge variant="destructive" className="ml-1.5 mt-0.5 text-[9px] h-4 px-1.5 align-middle">
                                İşten ayrıldı
                              </Badge>
                            ) : null}
                            {r.roster_zero_fill ? (
                              <span className={`ml-1.5 text-[10px] font-normal ${labelClass}`} title="Seçili dönemde ciro Excel kaydı yok">
                                (Excel kaydı yok)
                              </span>
                            ) : null}
                            {r.work_duration ? (
                              <span className={`block text-[10px] font-normal mt-0.5 ${labelClass}`}>
                                {r.work_duration}
                              </span>
                            ) : null}
                            {r.eski_data_aliyor ? (
                              <Badge className="ml-0 mt-0.5 text-[9px] h-4" variant="secondary">eskidata</Badge>
                            ) : null}
                            {isFormerLeaderSegment ? (
                              <span
                                className={`ml-1.5 text-[10px] font-semibold ${ciroRowLeftClass(isDark)}`}
                                title={segRange ? `Eski takım lideri · ${segRange}` : "Eski takım lideri dönemi"}
                              >
                                ({r.segment_leader_name})
                              </span>
                            ) : null}
                            {left && exitLabel ? (
                              <span className={`ml-1.5 text-[10px] font-normal ${isDark ? "text-rose-300/75" : "text-rose-500"}`}>
                                (çıkış {formatTlpDisplayDate(r.isten_cikis_tarihi)})
                              </span>
                            ) : null}
                          </td>
                          <td
                            className={`py-1 pr-2 ${r.split_by_leader ? (isDark ? "text-violet-300/95" : "text-violet-800") : labelClass}`}
                            title={leaderTitle}
                          >
                            <span className="font-medium">{r.segment_leader_name || r.former_team_leaders_label || "—"}</span>
                            {r.split_by_leader && segRange ? (
                              <span className={`block text-[10px] font-normal ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                                {segRange}
                              </span>
                            ) : null}
                          </td>
                          <td className="text-right tabular-nums">
                            <div className="flex flex-col items-end gap-0.5 min-w-[3rem]">
                              <span>{r.islem_adeti}</span>
                              {tlpOverview.maxIslem > 0 ? (
                                <TlpBarMeter
                                  value={r.islem_adeti}
                                  max={tlpOverview.maxIslem}
                                  barClass={tlpAccent("emerald", isDark).bar}
                                  isDark={isDark}
                                  className="w-14"
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className="text-right tabular-nums">{r.muayene_adeti}</td>
                          <td className="text-right tabular-nums">{r.iptal_adeti}</td>
                          <td className="text-right tabular-nums">{formatCiroFull(r.toplam_ciro)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </ModuleCard>

          <ModuleCard sectionId="tlp-mod-2" accent="violet" title="2 — MR alma (günlük)" icon={Activity} isDark={isDark} hint={modules.mr?.hint}>
            {!modules.mr || modules.mr.empty ? (
              <p className={`text-sm ${labelClass}`}>{modules.mr?.hint || "MR verisi yok."}</p>
            ) : (
              <>
                {(modules.mr.daily || []).length > 0 ? (
                  <div className="mb-4">
                    <TlpMrDailyAreaChart
                      daily={modules.mr.daily}
                      totalNet={modules.mr.total_net}
                      dailyAvgNet={modules.mr.daily_avg_net}
                      isDark={isDark}
                      textClass={textClass}
                      labelClass={labelClass}
                    />
                  </div>
                ) : null}
                {(modules.mr.agents || []).length > 0 && (
                  <div className="max-h-72 overflow-auto space-y-2 border-t pt-3">
                    <p className={`text-[11px] font-semibold mb-1 ${textClass}`}>Asistan bazında</p>
                    {modules.mr.sf_datam?.note ? (
                      <p className={`text-[10px] mb-2 ${labelClass}`}>{modules.mr.sf_datam.note}</p>
                    ) : null}
                    {modules.mr.agents.map((a) => (
                      <div
                        key={`${a.agent_id}-${a.assistant_name}`}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${isDark ? "border-slate-800 bg-slate-900/35" : "border-gray-100 bg-gray-50/90"}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className={`text-xs font-semibold truncate max-w-[12rem] ${textClass}`}>{a.assistant_name}</span>
                            <span
                              className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md shrink-0 ${
                                a.mr_alma_orani_display
                                  ? (isDark ? "bg-sky-500/15 text-sky-200 ring-1 ring-sky-500/30" : "bg-sky-50 text-sky-800 ring-1 ring-sky-200")
                                  : (isDark ? "bg-slate-800 text-slate-500" : "bg-gray-100 text-gray-500")
                              }`}
                              title="Toplam Data MR — MR alma oranı (seçili dönem)"
                            >
                              MR alma: {a.mr_alma_orani_display || (a.sf_datam_synced && !a.sf_datam_has_row ? "SF eşleşmedi" : "—")}
                            </span>
                          </div>
                          <div className={`text-[10px] mt-0.5 ${labelClass}`}>
                            Ort. {a.daily_avg_net}/gün · Toplam {a.total_net}
                          </div>
                        </div>
                        <TlpMrSparkline days={a.days} isDark={isDark} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </ModuleCard>

          <ModuleCard
            sectionId="tlp-mod-3"
            accent="teal"
            title="3 — WP & dış arama dönüşümü (takım)"
            icon={Phone}
            isDark={isDark}
            hint={modules.kaliteli_conversion?.note || "Özel Asistan Raporu 2 · Kaliteli Data (6’lı rapor alanları)"}
          >
            {(modules.kaliteli_conversion?.empty && !(modules.kaliteli_conversion?.batches || []).length) ? (
              <p className={`text-sm ${labelClass}`}>
                {modules.kaliteli_conversion?.hint || "Kaliteli Data yüklenmemiş."}
              </p>
            ) : (
              <>
                <p className={`text-[11px] mb-2 ${labelClass}`}>
                  Takım ortalaması: WP — WP dönüşüm işlem oranı; «Dış arama dönüşüm» sütunu — Özel Asistan Kaliteli Data «Kaliteli data işlem oranı» (%0 dahil).
                  {" "}
                  {modules.kaliteli_conversion?.note ? (
                    <span className={isDark ? "text-slate-400" : "text-gray-600"}>{modules.kaliteli_conversion.note}</span>
                  ) : null}
                </p>
                {modules.kaliteli_conversion?.empty && modules.kaliteli_conversion?.hint ? (
                  <p className={`text-sm mb-2 ${labelClass}`}>{modules.kaliteli_conversion.hint}</p>
                ) : null}
                {(modules.kaliteli_conversion?.teams || []).length === 0
                  && (modules.kaliteli_conversion?.batches || []).length > 0 && (
                  <div className={`mb-3 max-w-md ${labelClass}`} onClick={(e) => e.stopPropagation()}>
                    <p className="text-[10px] mb-1">Kaliteli dönem</p>
                    <Select
                      value={kaliteliBatchId
                        ? String(kaliteliBatchId)
                        : (modules.kaliteli_conversion?.selected_batch_id
                          ? String(modules.kaliteli_conversion.selected_batch_id)
                          : KALITELI_BATCH_LATEST)}
                      disabled={kaliteliRefreshing || loading}
                      onValueChange={onKaliteliBatchChange}
                    >
                      <SelectTrigger className={`h-9 text-xs w-full ${isDark ? "bg-slate-900 border-slate-700" : ""}`}>
                        <SelectValue placeholder="Dönem seçin" />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800 max-h-72" : "max-h-72"}>
                        <SelectItem value={KALITELI_BATCH_LATEST}>En son import (agent bazında)</SelectItem>
                        {(modules.kaliteli_conversion?.batches || []).map((b) => (
                          <SelectItem key={b.id} value={String(b.id)}>{formatKaliteliBatchOption(b)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="overflow-x-auto rounded-lg border text-xs">
                  <table className="w-full min-w-[580px]">
                    <thead className={isDark ? "bg-slate-800/80 text-slate-300" : "bg-emerald-800 text-white"}>
                      <tr>
                        <th className="text-left px-2 py-2 font-semibold">Takım</th>
                        <th className="text-left px-2 py-2 font-semibold hidden md:table-cell">Kaliteli dönem</th>
                        <th className="text-right px-2 py-2 font-semibold" title="Kaliteli Data — WP toplam data">WP sayısı</th>
                        <th className="text-right px-2 py-2 font-semibold">WP dönüşüm</th>
                        <th className="text-right px-2 py-2 font-semibold" title="Kaliteli Data — atanan kaliteli data">Kaliteli data</th>
                        <th className="text-right px-2 py-2 font-semibold" title="Kaliteli Data — Kaliteli data işlem oranı">Dış arama dönüşüm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(modules.kaliteli_conversion?.teams || []).map((t, teamIndex) => {
                        const open = kaliteliTeamOpen.has(String(t.team_id));
                        const agents = t.agents || [];
                        const kaliteliBatches = modules.kaliteli_conversion?.batches || [];
                        const effectiveBatchId = kaliteliBatchId
                          ?? modules.kaliteli_conversion?.selected_batch_id
                          ?? null;
                        const teamPeriodKeys = new Set(
                          agents
                            .filter((a) => a.has_kaliteli && a.kaliteli_period_start)
                            .map((a) => `${a.kaliteli_period_start}|${a.kaliteli_period_end}`)
                        );
                        const teamPeriodLabel = teamPeriodKeys.size === 1
                          ? formatKaliteliPeriodLabel(
                            agents.find((a) => a.kaliteli_period_start)?.kaliteli_period_start,
                            agents.find((a) => a.kaliteli_period_start)?.kaliteli_period_end,
                            agents.find((a) => a.kaliteli_sheet_title)?.kaliteli_sheet_title,
                          )
                          : teamPeriodKeys.size > 1 ? "Agent bazında farklı" : "—";
                        const selectValue = effectiveBatchId
                          ? String(effectiveBatchId)
                          : KALITELI_BATCH_LATEST;
                        const teamWpCount = agents.some((a) => a.wp_data_count != null)
                          ? agents.reduce((s, a) => s + (Number(a.wp_data_count) || 0), 0)
                          : null;
                        const teamKalCount = agents.some((a) => a.kaliteli_data_count != null)
                          ? agents.reduce((s, a) => s + (Number(a.kaliteli_data_count) || 0), 0)
                          : null;
                        return (
                          <React.Fragment key={t.team_id}>
                            <tr
                              className={`cursor-pointer ${isDark ? "border-t border-slate-800 hover:bg-slate-800/40" : "border-t border-gray-100 hover:bg-gray-50"}`}
                              onClick={() => toggleKaliteliTeam(t.team_id)}
                            >
                              <td className={`px-2 py-1.5 ${textClass}`}>
                                <span className="inline-flex items-center gap-1">
                                  {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-70" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-70" />}
                                  {t.team_name}
                                </span>
                              </td>
                              <td
                                className={`px-2 py-1.5 hidden md:table-cell min-w-[12rem] max-w-[18rem] ${labelClass}`}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                {teamIndex === 0 && kaliteliBatches.length > 0 ? (
                                  <Select
                                    value={selectValue}
                                    disabled={kaliteliRefreshing || loading}
                                    onValueChange={onKaliteliBatchChange}
                                  >
                                    <SelectTrigger className={`h-8 text-[11px] w-full ${isDark ? "bg-slate-900 border-slate-700" : "bg-white"}`}>
                                      <SelectValue placeholder="Kaliteli dönem" />
                                    </SelectTrigger>
                                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800 max-h-72" : "max-h-72"}>
                                      <SelectItem value={KALITELI_BATCH_LATEST}>
                                        En son import (agent bazında)
                                      </SelectItem>
                                      {kaliteliBatches.map((b) => (
                                        <SelectItem key={b.id} value={String(b.id)}>
                                          {formatKaliteliBatchOption(b)}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <span title="Seçili Kaliteli dönem">{teamPeriodLabel}</span>
                                )}
                              </td>
                              <td className={`text-right tabular-nums px-2 py-1.5 font-medium ${textClass}`}>
                                {teamWpCount != null ? formatKaliteliCount(teamWpCount) : "—"}
                              </td>
                              <td className="text-right tabular-nums px-2 py-1.5 font-medium">
                                <div className="inline-flex flex-col items-end gap-1 min-w-[4.5rem]">
                                  <span>{t.wp_conversion_avg != null ? `%${t.wp_conversion_avg}` : "—"}</span>
                                  {t.wp_conversion_avg != null ? (
                                    <TlpBarMeter value={t.wp_conversion_avg} max={100} barClass={tlpAccent("teal", isDark).bar} isDark={isDark} className="w-full" />
                                  ) : null}
                                </div>
                              </td>
                              <td className={`text-right tabular-nums px-2 py-1.5 font-medium ${textClass}`}>
                                {teamKalCount != null ? formatKaliteliCount(teamKalCount) : "—"}
                              </td>
                              <td className="text-right tabular-nums px-2 py-1.5 font-medium">
                                <div className="inline-flex flex-col items-end gap-1 min-w-[4.5rem]">
                                  <span>{t.dis_arama_conversion_avg != null ? `%${t.dis_arama_conversion_avg}` : "—"}</span>
                                  {t.dis_arama_conversion_avg != null ? (
                                    <TlpBarMeter value={t.dis_arama_conversion_avg} max={100} barClass={tlpAccent("sky", isDark).bar} isDark={isDark} className="w-full" />
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                            {open && agents.map((a) => (
                              <tr
                                key={`${t.team_id}-${a.agent_id}`}
                                className={isDark ? "border-t border-slate-800/50 bg-slate-900/30" : "border-t border-gray-50 bg-gray-50/80"}
                              >
                                <td className={`pl-7 pr-2 py-1 ${textClass}`}>
                                  {a.agent_name}
                                  {!a.has_kaliteli ? (
                                    <span className={`ml-1 text-[10px] ${labelClass}`}>(Kaliteli yok)</span>
                                  ) : null}
                                </td>
                                <td className="hidden md:table-cell" aria-hidden="true" />
                                <td className={`text-right tabular-nums px-2 py-1 ${a.wp_data_count != null ? textClass : labelClass}`}>
                                  {formatKaliteliCount(a.wp_data_count)}
                                </td>
                                <td className={`text-right tabular-nums px-2 py-1 ${a.wp_in_avg ? textClass : labelClass}`}>
                                  {a.wp_conversion != null ? `%${a.wp_conversion}` : "—"}
                                </td>
                                <td className={`text-right tabular-nums px-2 py-1 ${a.kaliteli_data_count != null ? textClass : labelClass}`}>
                                  {formatKaliteliCount(a.kaliteli_data_count)}
                                </td>
                                <td className={`text-right tabular-nums px-2 py-1 ${a.dis_in_avg ? textClass : labelClass}`}>
                                  {a.dis_arama_conversion != null ? `%${a.dis_arama_conversion}` : "—"}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </ModuleCard>

          <ModuleCard sectionId="tlp-mod-4" accent="sky" title="4 — Ort. görüşme & mola" icon={Timer} isDark={isDark} hint="Agent görüşme raporu · eski data etiketi agent kartından">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <TlpKpiCard
                icon={Phone}
                label="Takım ort. görüşme / gün"
                value={formatSeconds(modules.call_metrics?.team_avg_daily_talk_seconds)}
                accentKey="sky"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
              <TlpKpiCard
                icon={Timer}
                label="Takım ort. mola / gün"
                value={formatSeconds(modules.call_metrics?.team_avg_daily_break_seconds)}
                accentKey="amber"
                isDark={isDark}
                textClass={textClass}
                labelClass={labelClass}
              />
            </div>
            <div className="max-h-64 overflow-auto text-xs space-y-2">
              {(modules.call_metrics?.agents || []).map((a) => (
                <div key={a.agent_id} className={`rounded-lg px-2 py-2 ${isDark ? "bg-slate-800/40" : "bg-gray-50"}`}>
                  <div className="flex justify-between gap-2 mb-1.5">
                    <span className={`font-medium ${textClass}`}>
                      {a.agent_name}
                      {a.eski_data_aliyor ? <Badge className="ml-1 text-[9px] h-4" variant="secondary">eski data alıyor</Badge> : null}
                    </span>
                    <span className={`tabular-nums shrink-0 ${labelClass}`}>
                      {formatSeconds(a.avg_daily_talk_seconds)} / {formatSeconds(a.avg_daily_break_seconds)}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-14 text-[9px] ${labelClass}`}>Görüşme</span>
                      <TlpBarMeter
                        value={a.avg_daily_talk_seconds}
                        max={tlpOverview.maxTalk || 1}
                        barClass={tlpAccent("sky", isDark).bar}
                        isDark={isDark}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-14 text-[9px] ${labelClass}`}>Mola</span>
                      <TlpBarMeter
                        value={a.avg_daily_break_seconds}
                        max={Math.max(...(modules.call_metrics?.agents || []).map((x) => Number(x.avg_daily_break_seconds) || 0), 1)}
                        barClass={tlpAccent("amber", isDark).bar}
                        isDark={isDark}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ModuleCard>

          <ModuleCard sectionId="tlp-mod-5" accent="blue" title="5 — TL × Kalite eşleşme" icon={Scale} isDark={isDark} hint="Özel Asistan Raporu 2 ile aynı 5 kriter; uyumsuz satırlar kırmızı">
            {tlpOverview.kaliteAvg != null ? (
              <div className={`mb-3 flex items-center gap-3 rounded-xl border px-3 py-2 ${isDark ? "border-blue-500/25 bg-blue-950/20" : "border-blue-100 bg-blue-50/60"}`}>
                <div className={`text-2xl font-bold tabular-nums ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                  %{tlpOverview.kaliteAvg}
                </div>
                <div className={`text-[11px] ${labelClass}`}>
                  Takım ortalaması TL × Kalite uyumu
                  {tlpOverview.kaliteMismatch ? ` · ${tlpOverview.kaliteMismatch} uyumsuz kriter kaydı` : ""}
                </div>
              </div>
            ) : null}
            <div className="max-h-[28rem] overflow-auto text-xs space-y-1.5">
              {(modules.tl_kalite_alignment?.agents || []).map((a) => {
                const al = a.alignment;
                const diffs = al?.diffs || (al?.criteria || []).filter((c) => c.mismatch).map((c) => c.label);
                const expanded = tlKaliteExpanded.has(a.agent_id);
                const canExpand = al?.criteria?.length > 0 || al?.percent != null;
                return (
                  <div
                    key={a.agent_id}
                    className={`rounded-lg px-2 py-1.5 ${isDark ? "bg-slate-800/40" : "bg-gray-50"}`}
                  >
                    <button
                      type="button"
                      disabled={!canExpand}
                      className={`w-full flex items-center gap-2 text-left ${canExpand ? "cursor-pointer" : "cursor-default opacity-80"}`}
                      onClick={() => {
                        if (!canExpand) return;
                        setTlKaliteExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(a.agent_id)) next.delete(a.agent_id);
                          else next.add(a.agent_id);
                          return next;
                        });
                      }}
                    >
                      {canExpand ? (
                        expanded ? <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-60" />
                      ) : (
                        <span className="w-3.5 shrink-0" />
                      )}
                      <span className={`flex-1 min-w-0 truncate ${textClass}`}>
                        {a.agent_name}
                        {a.eski_data_aliyor ? <Badge className="ml-1 text-[9px] h-4" variant="secondary">eski data</Badge> : null}
                      </span>
                      <span className={`shrink-0 tabular-nums font-semibold ${labelClass}`}>
                        {al?.percent != null ? `%${al.percent}` : "—"}
                      </span>
                    </button>
                    {!expanded && diffs.length > 0 ? (
                      <div className="flex flex-wrap gap-1 mt-1 pl-5">
                        {diffs.map((d) => (
                          <Badge
                            key={d}
                            variant="outline"
                            className={`text-[9px] h-5 ${isDark ? "border-rose-500/40 text-rose-300 bg-rose-500/10" : "border-rose-300 text-rose-700 bg-rose-50"}`}
                          >
                            Uyumsuz: {d}
                          </Badge>
                        ))}
                      </div>
                    ) : null}
                    {expanded && al?.criteria?.length ? (
                      <>
                        {al.matched_report_date ? (
                          <p className={`text-[10px] mt-1 pl-5 ${isDark ? "text-teal-300/80" : "text-teal-700"}`}>
                            Eşleşen dönem: {formatReportDate(al.matched_report_date)}
                          </p>
                        ) : null}
                        <div className="pl-4 pr-1">
                          <TlpKaliteCriteriaGrid criteria={al.criteria} isDark={isDark} labelClass={labelClass} />
                        </div>
                      </>
                    ) : null}
                    {!al ? (
                      <p className={`text-[10px] pl-5 mt-0.5 ${labelClass}`}>TL veya Kalite kaydı yok.</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </ModuleCard>

          <ModuleCard
            sectionId="tlp-mod-6"
            accent="amber"
            title="6 — Personel giriş / çıkış"
            icon={ClipboardList}
            isDark={isDark}
            hint={modules.personnel?.note || "Agent işe giriş ve işten çıkış tarihleri"}
          >
            {!modules.personnel || modules.personnel.empty ? (
              <p className={`text-sm ${labelClass}`}>{modules.personnel?.hint || "Personel verisi yok."}</p>
            ) : (
              <>
                {(modules.personnel.summary?.avg_exit_in_period_tenure_days != null
                  || modules.personnel.summary?.avg_exit_tenure_days != null) ? (
                  <div className={`mb-3 rounded-lg border px-3 py-2.5 text-xs ${isDark ? "border-amber-500/35 bg-amber-950/25 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-950"}`}>
                    {modules.personnel.summary?.avg_exit_in_period_tenure_days != null ? (
                      <p className="font-medium">
                        Seçili dönemde işten çıkanlar — ortalama çalışma süresi:{" "}
                        <span className="tabular-nums">{modules.personnel.summary.avg_exit_in_period_tenure_days} gün</span>
                        {modules.personnel.summary.avg_exit_in_period_tenure_label ? (
                          <span className={`font-normal ml-1 ${isDark ? "text-amber-200/80" : "text-amber-800"}`}>
                            ({modules.personnel.summary.avg_exit_in_period_tenure_label})
                          </span>
                        ) : null}
                        {" · "}
                        {modules.personnel.summary.exit_in_period_with_tenure ?? modules.personnel.summary.exit_in_period ?? 0} kişi
                      </p>
                    ) : null}
                    {modules.personnel.summary?.avg_exit_in_period_tenure_days == null
                      && modules.personnel.summary?.avg_exit_tenure_days != null ? (
                        <p className={isDark ? "text-amber-200/90" : "text-amber-900"}>
                          Dönemde çıkış yok; tüm işten ayrılanlar (giriş→çıkış) ortalaması:{" "}
                          <strong className="tabular-nums">{modules.personnel.summary.avg_exit_tenure_days} gün</strong>
                          {modules.personnel.summary.avg_exit_tenure_label ? (
                            <span className="font-normal ml-1">({modules.personnel.summary.avg_exit_tenure_label})</span>
                          ) : null}
                        </p>
                      ) : null}
                    <p className={`text-[10px] mt-1 ${isDark ? "text-amber-200/70" : "text-amber-800/80"}`}>
                      Takım liderinin çıkış kararlarının erken / geç eğilimini okumak için: düşük gün erken ayrılış, yüksek gün daha uzun kalış.
                    </p>
                  </div>
                ) : null}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <TlpKpiCard icon={Users} label="Dönemde giriş" value={modules.personnel.summary?.hire_in_period ?? 0} accentKey="emerald" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  <TlpKpiCard icon={CircleX} label="Dönemde çıkış" value={modules.personnel.summary?.exit_in_period ?? 0} accentKey="amber" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  <TlpKpiCard icon={Users} label="Aktif" value={modules.personnel.summary?.active_count ?? 0} accentKey="sky" isDark={isDark} textClass={textClass} labelClass={labelClass} />
                  <TlpKpiCard
                    icon={Timer}
                    label="Ortalama işten çıkarma süresi"
                    value={modules.personnel.summary?.avg_exit_in_period_tenure_days != null
                      ? `${modules.personnel.summary.avg_exit_in_period_tenure_days} gün`
                      : (modules.personnel.summary?.avg_exit_tenure_days != null
                        ? `${modules.personnel.summary.avg_exit_tenure_days} gün`
                        : "—")}
                    hint={modules.personnel.summary?.avg_exit_in_period_tenure_label || modules.personnel.summary?.avg_exit_tenure_label || undefined}
                    accentKey="amber"
                    isDark={isDark}
                    textClass={textClass}
                    labelClass={labelClass}
                  />
                </div>
                {(modules.personnel.teams || []).length > 1 && (
                  <div className="mb-3 overflow-x-auto rounded-lg border text-xs">
                    <table className="w-full min-w-[360px]">
                      <thead className={isDark ? "bg-slate-800/80 text-slate-300" : "bg-emerald-800 text-white"}>
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold">Takım</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Giriş</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Çıkış</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Aktif</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modules.personnel.teams.map((t) => (
                          <tr key={t.team_id} className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}>
                            <td className={`px-2 py-1 ${textClass}`}>{t.team_name}</td>
                            <td className="text-right tabular-nums px-2 py-1">{t.hire_in_period}</td>
                            <td className="text-right tabular-nums px-2 py-1">{t.exit_in_period}</td>
                            <td className="text-right tabular-nums px-2 py-1">{t.active_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="max-h-64 overflow-auto rounded-lg border text-xs">
                  <table className="w-full min-w-[520px]">
                    <thead className={`sticky top-0 ${isDark ? "bg-slate-800/95 text-slate-300" : "bg-gray-100 text-gray-700"}`}>
                      <tr>
                        <th className="text-left px-2 py-1.5 font-semibold">Asistan</th>
                        <th className="text-left px-2 py-1.5 font-semibold hidden md:table-cell">Takım</th>
                        <th className="text-left px-2 py-1.5 font-semibold">İşe giriş</th>
                        <th className="text-left px-2 py-1.5 font-semibold">İşten çıkış</th>
                        <th className="text-left px-2 py-1.5 font-semibold">Çalışma süresi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPersonnelAgents.map((a) => (
                        <tr
                          key={a.agent_id}
                          className={isDark ? "border-t border-slate-800" : "border-t border-gray-100"}
                        >
                          <td className={`px-2 py-1 ${!a.is_active ? ciroRowLeftClass(isDark) : textClass}`}>
                            {a.agent_name}
                            {!a.is_active ? (
                              <Badge className="ml-1 text-[9px] h-4" variant="secondary">ayrıldı</Badge>
                            ) : null}
                          </td>
                          <td className={`px-2 py-1 hidden md:table-cell ${labelClass}`}>{a.team_name}</td>
                          <td className="px-2 py-1 tabular-nums whitespace-nowrap">
                            {a.hire_date || "—"}
                            {a.hire_in_period ? <span className="text-emerald-500 ml-0.5">•</span> : null}
                          </td>
                          <td className="px-2 py-1 tabular-nums whitespace-nowrap">
                            {a.exit_date || "—"}
                            {a.exit_in_period ? <span className="text-amber-500 ml-0.5">•</span> : null}
                          </td>
                          <td className={`px-2 py-1 ${labelClass}`}>
                            {a.exit_date
                              ? (a.tenure_until_exit_label || "—")
                              : (a.tenure_label || "—")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className={`text-[10px] mt-2 ${labelClass}`}>
                  • yeşil nokta: giriş seçili dönemde · turuncu nokta: çıkış seçili dönemde. Aktifler üstte (en uzun kıdem önce); ayrılanlar altta (en kısa çalışma süresi önce).
                </p>
              </>
            )}
          </ModuleCard>

        </div>
        </div>
      ) : !loading && !isCompareMode ? (
        <p className={`text-sm text-center py-12 ${labelClass}`}>
          Takım lideri seçin (kıyas için birden fazla işaretleyin), tarih aralığı belirleyip Sorgula ile raporu oluşturun.
        </p>
      ) : null}

      <MrExcelImportModal
        open={mrModalOpen}
        onClose={closeMrModal}
        fileName={mrFileName}
        parsed={mrParsed}
        parsing={mrParsing}
        agents={companyAgents}
        defaultYear={mrDefaultYear}
        companyMissing={!effectiveCompanyId}
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        importing={mrImporting}
        onConfirm={confirmMrImport}
        importResult={mrImportResult}
      />
    </div>
  );
};

export default TeamLeaderPerformanceReportPage;
